/**
 * Task/Case Details — view models aligned with CMS Data Dictionary V 0.1
 * and extranet criminal case request fields (steps 01–04).
 */

export type WfNodeKind = 'done' | 'curr' | 'pend';

export type TaskWorkflowStep = {
  kind: WfNodeKind;
  title: string;
  line1: string;
  line2: string;
};

export type TaskCaseVariant = 'active' | 'rejected' | 'closure';

export type AccusedRecord = { name: string; status: string };

/** Criminal case intake fields (Data Dictionary — Case Request steps 1–4). */
export type CriminalCaseDetail = {
  fileCategory: string;
  subFileCategory: string;
  applicableAreas: string[];
  description: string;
  caseSource: string;
  province: string;
  district: string;
  highCourtName: string;
  magistrateCourtName: string;
  policeStation: string;
  yourReferenceNumber: string;
  otherExternalReferences: string;
  caseNumber: string;
  hasConnectedMatters: 'Yes' | 'No';
  connectedCaseNumber: string;
  connectedAgdFileRef: string;
  victimName: string;
  victimDob: string;
  victimAge: number;
  victimGender: string;
  offenceDate: string;
  placeOfOffence: string;
  accused: AccusedRecord[];
  urgencyLevel: string;
  deadline: string;
  urgencyJustification: string;
  submittedAgencyName: string;
  submittedDivision: string;
  designation: string;
  submittedBy: string;
  email: string;
  contactNumber: string;
  deliveryMethod: string;
};

export type SubmittedDocumentRow = {
  id: string;
  documentTypeName: string;
  status: 'uploaded' | 'pending' | 'optional';
  fileName: string;
  fileSize: string;
  reason: string;
  uploadedAt: string;
};

export type AdditionalDocumentRow = {
  id: string;
  fileName: string;
  fileSize: string;
  uploadedAt: string;
};

export type TaskCommMessage = { id: string; sender: string; time: string; body: string };

export type TaskMinuteEntry = { user: string; time: string; text: string };

export type TaskSubTaskRow = {
  ref: string;
  title: string;
  assignedTo: string;
  status: string;
  dueDate: string;
};

export type TaskFmlEvent = 'qrin' | 'qrout';

export type TaskFmlRow = {
  date: string;
  time: string;
  event: TaskFmlEvent;
  name: string;
  role: string;
  note: string;
};

export type TaskAuditAction = 'upd' | 'view' | 'fwd' | 'cre';

export type TaskAuditRow = {
  date: string;
  time: string;
  actor: string;
  role: string;
  action: TaskAuditAction;
  actionLabel: string;
};

export type TaskRejectionInfo = {
  reason: string;
  at: string;
  by: string;
};

export type TaskCaseDetailView = {
  variant: TaskCaseVariant;
  /** Case request category UUID (needed for workflow pop-ups like Forward Task). */
  fileCategoryId?: string;
  /** Case request sub-category UUID (needed for workflow pop-ups like Forward Task). */
  subFileCategoryId?: string;
  /** Header task type (REGULAR / REVIEW / SUBSIDIARY). */
  taskType?: string;
  /** True when this task was received via forward (CMS-CRC-20 `is_received_via_forward`). */
  isReceivedViaForward?: boolean;
  /** CMS-CRC-20 — user who created/assigned the current task (subsidiary revert). */
  taskCreatedById?: string;
  taskCreatedByName?: string;
  fileRef: string;
  fileCategory: string;
  subFileCategory: string;
  currentStage: string;
  taskStatus: string;
  taskStatusClass: string;
  custodyStatus: string;
  custodyClass: string;
  linkedFileRef: string;
  showWorkflow: boolean;
  showWorkflowActions: boolean;
  workflowActionMode: 'submit' | 'complete';
  /** True when the current workflow stage is Closure (header or CMS-CRC-21 `is_current`). */
  currentStageIsClosure: boolean;
  /** True when CMS-CRC-21 workflow progress shows Closure as the stage after the current one. */
  nextStageIsClosure: boolean;
  workflowSteps: TaskWorkflowStep[];
  submittedAt: string;
  recentSubmittedAt: string;
  rejection?: TaskRejectionInfo;
  case: CriminalCaseDetail;
  submittedDocuments: SubmittedDocumentRow[];
  additionalDocuments: AdditionalDocumentRow[];
  commMessages: TaskCommMessage[];
  minuteEntries: TaskMinuteEntry[];
  subTasks: TaskSubTaskRow[];
  fileMovement: TaskFmlRow[];
  auditLog: TaskAuditRow[];
  assigneeOfficerOptions: string[];
  documentMoverOptions: string[];
};

export function emptyCriminalCaseDetail(): CriminalCaseDetail {
  return {
    fileCategory: '',
    subFileCategory: '',
    applicableAreas: [],
    description: '',
    caseSource: '',
    province: '',
    district: '',
    highCourtName: '',
    magistrateCourtName: '',
    policeStation: '',
    yourReferenceNumber: '',
    otherExternalReferences: '',
    caseNumber: '',
    hasConnectedMatters: 'No',
    connectedCaseNumber: '',
    connectedAgdFileRef: '',
    victimName: '',
    victimDob: '',
    victimAge: 0,
    victimGender: '',
    offenceDate: '',
    placeOfOffence: '',
    accused: [],
    urgencyLevel: '',
    deadline: '',
    urgencyJustification: '',
    submittedAgencyName: '',
    submittedDivision: '',
    designation: '',
    submittedBy: '',
    email: '',
    contactNumber: '',
    deliveryMethod: '',
  };
}

/** Empty shell used before API data loads — no placeholder/mock field values. */
export function createEmptyTaskCaseDetailView(): TaskCaseDetailView {
  return {
    variant: 'active',
    fileCategoryId: '',
    subFileCategoryId: '',
    taskType: '',
    isReceivedViaForward: false,
    taskCreatedById: '',
    taskCreatedByName: '',
    fileRef: '',
    fileCategory: '',
    subFileCategory: '',
    currentStage: '',
    taskStatus: '',
    taskStatusClass: 'is-default',
    custodyStatus: '',
    custodyClass: 'is-default',
    linkedFileRef: '',
    showWorkflow: false,
    showWorkflowActions: false,
    workflowActionMode: 'submit',
    currentStageIsClosure: false,
    nextStageIsClosure: false,
    workflowSteps: [],
    submittedAt: '',
    recentSubmittedAt: '',
    case: emptyCriminalCaseDetail(),
    submittedDocuments: [],
    additionalDocuments: [],
    commMessages: [],
    minuteEntries: [],
    subTasks: [],
    fileMovement: [],
    auditLog: [],
    assigneeOfficerOptions: [],
    documentMoverOptions: [],
  };
}

export function statusPillClass(statusName: string): string {
  const s = statusName.toLowerCase();
  if (s.includes('progress') || s.includes('review')) return 'is-progress';
  if (s.includes('reject')) return 'is-reject';
  if (s.includes('complete') || s.includes('closed')) return 'is-complete';
  return 'is-default';
}
